import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchForm from '../components/SearchForm';
import { createClient } from '@supabase/supabase-js';
import { History, Loader } from 'lucide-react';
import { analyzeLocation, getSearchFromCache } from '../services/locationService';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

interface SearchHistory {
  id: string;
  location: string;
  business_type: string;
  budget: string;
  created_at: string;
}

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchHistory, setSearchHistory] = useState<SearchHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  useEffect(() => {
    checkAuth();
    loadSearchHistory();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login', { state: { returnTo: '/search' } });
    }
  };

  const loadSearchHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('search_history')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Deduplicate search history based on location, business_type, and budget
      const uniqueSearches = data?.reduce((acc: SearchHistory[], current) => {
        const isDuplicate = acc.some(item => 
          item.location.toLowerCase() === current.location.toLowerCase() &&
          item.business_type.toLowerCase() === current.business_type.toLowerCase() &&
          item.budget.toLowerCase() === current.budget.toLowerCase()
        );
        
        if (!isDuplicate) {
          acc.push(current);
        }
        return acc;
      }, []) || [];

      setSearchHistory(uniqueSearches.slice(0, 5)); // Keep only the 5 most recent unique searches
    } catch (error) {
      console.error('Error loading search history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSearch = async (searchData: {
    location: string;
    businessType: string;
    budget: string;
  }) => {
    setLoading(true);
    setAnalysisError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login', { state: { returnTo: '/search' } });
        return;
      }

      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Check if this exact search already exists
      const { data: existingSearches } = await supabase
        .from('search_history')
        .select('*')
        .eq('location', searchData.location)
        .eq('business_type', searchData.businessType)
        .eq('budget', searchData.budget)
        .eq('user_id', userData.user.id);

      // Only save to history if this is a new unique search
      if (!existingSearches?.length) {
        const { data: historyData, error: historyError } = await supabase
          .from('search_history')
          .insert([
            {
              location: searchData.location,
              business_type: searchData.businessType,
              budget: searchData.budget,
              user_id: userData.user.id
            }
          ])
          .select();

        if (historyError) throw historyError;
      }

      // Perform AI analysis
      const analysis = await analyzeLocation(
        searchData.location,
        searchData.businessType,
        searchData.budget
      );

      // Save to location analysis table
      const { data, error } = await supabase
        .from('location_analysis')
        .insert([
          {
            location: searchData.location,
            business_type: searchData.businessType,
            budget: searchData.budget,
            user_id: userData.user.id
          }
        ])
        .select();

      if (error) throw error;

      // Reload search history
      await loadSearchHistory();

      // Navigate to results with analysis data
      navigate('/results', { 
        state: { 
          searchData,
          analysisId: data[0].id,
          analysis 
        } 
      });
    } catch (error) {
      console.error('Error analyzing location:', error);
      setAnalysisError('Failed to analyze location. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryClick = async (history: SearchHistory) => {
    setLoading(true);
    setAnalysisError(null);
    
    try {
      const searchData = {
        location: history.location,
        businessType: history.business_type,
        budget: history.budget
      };
      
      const analysis = await analyzeLocation(
        searchData.location,
        searchData.businessType,
        searchData.budget
      );

      navigate('/results', { 
        state: { 
          searchData,
          analysis 
        } 
      });
    } catch (error) {
      console.error('Error analyzing location:', error);
      setAnalysisError('Failed to analyze location. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold text-primary">Location Scout</h1>
        <p className="text-muted-foreground">
          Enter your criteria and let our AI analyze the perfect location for your business
        </p>
      </div>

      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <SearchForm onSubmit={handleSearch} isLoading={loading} />
        {analysisError && (
          <div className="mt-4 p-4 bg-destructive/10 text-destructive rounded-lg">
            {analysisError}
          </div>
        )}
      </div>

      {loadingHistory ? (
        <div className="flex justify-center">
          <Loader className="animate-spin text-primary" size={24} />
        </div>
      ) : searchHistory.length > 0 && (
        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <History size={20} className="mr-2 text-primary" />
            Recent Searches
          </h2>
          <div className="space-y-2">
            {searchHistory.map((history) => (
              <button
                key={history.id}
                onClick={() => handleHistoryClick(history)}
                className="w-full text-left p-3 rounded-lg hover:bg-card/50 transition-colors border border-border hover:border-primary/50"
              >
                <div className="font-medium">{history.location}</div>
                <div className="text-sm text-muted-foreground">
                  {history.business_type} • Budget: {history.budget}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {new Date(history.created_at).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center font-mono text-primary animate-pulse">
          <pre>
{`
 Analyzing Location...
 ==================
 Scanning area...
 Processing data...
 Computing metrics...
`}
          </pre>
        </div>
      )}
    </div>
  );
};

export default SearchPage;